import React, { useState, useEffect } from 'react';
import { Mail, Users, Send, CheckCircle2, XCircle, AlertCircle, Loader } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';

const EmailCampaign = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    loadPreview();
  }, []);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Use relative URL - works on any domain
      const response = await fetch('/api/email-campaign/preview', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreview(data);
      } else {
        throw new Error('Failed to load preview');
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      toast({
        title: "Error",
        description: "Failed to load user preview",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmails = async () => {
    if (!window.confirm(`Are you sure you want to send welcome emails to ${preview?.real_users_count} users?`)) {
      return;
    }

    setSending(true);
    setResult(null);

    try {
      const token = localStorage.getItem('token');
      // Use relative URL - works on any domain
      const response = await fetch('/api/email-campaign/send-welcome-emails', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setResult(data);
        
        toast({
          title: "Campaign Complete!",
          description: `Successfully sent ${data.success_count} emails`
        });
      } else {
        throw new Error('Failed to send emails');
      }
    } catch (error) {
      console.error('Error sending emails:', error);
      toast({
        title: "Error",
        description: "Failed to send emails. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-cyan-900 flex items-center justify-center">
        <div className="text-cyan-400 text-lg flex items-center gap-2">
          <Loader className="w-6 h-6 animate-spin" />
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-cyan-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-cyan-500/20 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-cyan-400 flex items-center gap-3">
                <Mail className="w-8 h-8" />
                Welcome Email Campaign
              </h1>
              <p className="text-cyan-600 mt-2">Send welcome emails to all real users</p>
            </div>
            <Button
              onClick={loadPreview}
              variant="outline"
              className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-cyan-500/30 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-cyan-600 text-sm font-medium">Total Users</p>
                <p className="text-4xl font-bold text-cyan-400 mt-2">{preview?.total_users || 0}</p>
              </div>
              <Users className="w-12 h-12 text-cyan-400 opacity-50" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 text-sm font-medium">Real Users</p>
                <p className="text-4xl font-bold text-green-400 mt-2">{preview?.real_users_count || 0}</p>
              </div>
              <CheckCircle2 className="w-12 h-12 text-green-400 opacity-50" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-600 text-sm font-medium">Test Users (Skipped)</p>
                <p className="text-4xl font-bold text-yellow-400 mt-2">{preview?.test_users_count || 0}</p>
              </div>
              <AlertCircle className="w-12 h-12 text-yellow-400 opacity-50" />
            </div>
          </div>
        </div>

        {/* User List */}
        {preview && preview.real_users_count > 0 && (
          <div className="bg-slate-800/50 backdrop-blur-sm border border-cyan-500/20 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-cyan-400 mb-4">
              Real Users ({preview.real_users_count})
            </h2>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-slate-900 border-b border-cyan-500/20">
                  <tr>
                    <th className="text-left text-cyan-600 p-3 text-sm font-medium">#</th>
                    <th className="text-left text-cyan-600 p-3 text-sm font-medium">Username</th>
                    <th className="text-left text-cyan-600 p-3 text-sm font-medium">Email</th>
                    <th className="text-left text-cyan-600 p-3 text-sm font-medium">Registered</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.real_users.map((user, index) => (
                    <tr key={user.user_id} className="border-b border-cyan-500/10 hover:bg-slate-700/30">
                      <td className="p-3 text-cyan-400">{index + 1}</td>
                      <td className="p-3 text-cyan-300">{user.username}</td>
                      <td className="p-3 text-cyan-500">{user.email}</td>
                      <td className="p-3 text-cyan-600 text-sm">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Send Button */}
        {preview && preview.real_users_count > 0 && !result && (
          <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-cyan-400">Ready to Send</h3>
                <p className="text-cyan-600 mt-1">
                  Welcome emails will be sent to {preview.real_users_count} users
                </p>
              </div>
              <Button
                onClick={handleSendEmails}
                disabled={sending}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-8 py-3 text-lg"
              >
                {sending ? (
                  <>
                    <Loader className="w-5 h-5 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Send Welcome Emails
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="bg-slate-800/50 backdrop-blur-sm border border-cyan-500/20 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-cyan-400 mb-6 flex items-center gap-2">
              <CheckCircle2 className="w-8 h-8 text-green-400" />
              Campaign Results
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4">
                <p className="text-green-600 text-sm font-medium">Successfully Sent</p>
                <p className="text-3xl font-bold text-green-400 mt-2">{result.success_count}</p>
              </div>

              <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
                <p className="text-red-600 text-sm font-medium">Failed</p>
                <p className="text-3xl font-bold text-red-400 mt-2">{result.failed_count}</p>
              </div>

              <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4">
                <p className="text-blue-600 text-sm font-medium">Total Processed</p>
                <p className="text-3xl font-bold text-blue-400 mt-2">{result.total_sent}</p>
              </div>
            </div>

            {result.failed_users && result.failed_users.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <h3 className="text-red-400 font-semibold mb-2 flex items-center gap-2">
                  <XCircle className="w-5 h-5" />
                  Failed Recipients ({result.failed_users.length})
                </h3>
                <ul className="text-red-500 text-sm space-y-1">
                  {result.failed_users.map((user, index) => (
                    <li key={index}>• {user}</li>
                  ))}
                </ul>
              </div>
            )}

            <Button
              onClick={loadPreview}
              className="mt-6 bg-cyan-500 hover:bg-cyan-600 text-white"
            >
              Send Another Campaign
            </Button>
          </div>
        )}

        {/* No Users */}
        {preview && preview.real_users_count === 0 && (
          <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-6 text-center">
            <AlertCircle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-yellow-400 mb-2">No Real Users Found</h3>
            <p className="text-yellow-600">All users appear to be test accounts</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailCampaign;
